/**
 * CommentCountBadge.tsx — 8214-A
 *
 * Shows comment icon + count in the editor formatting toolbar.
 * Clicking toggles the comment sidebar open/closed.
 *
 * Design ref: docs/feedback-project/designs/figma-code/with-comments.html lines 139-143
 */
import styled from "styled-components";
import { useToggleCommentMode } from "../../../hooks/useToggleCommentMode";
/*
 * The toolbar badge and sidebar must use one definition of visible Open comments. Raw server
 * totals include retained historical rows whose ranges are absent from this document version.
 */
import { getVisibleOpenCommentCount, useCommentStore } from "../../../stores/commentStore";
import { COMMENT_FILTER_TYPES, filterCommentsByType } from "../CommentSidebar/commentStatus";

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const BadgeButtonOuter = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  user-select: none;
  color: ${({ $isActive }) => ($isActive ? "var(--primary-cta-high-contrast)" : "var(--grey-2)")};

  &:hover,
  &:focus-visible {
    & > .comment-badge-inner {
      height: 32px;
      border-radius: 5px;
      background-color: var(--menu-color-hover);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--primary-cta-high-contrast);
    outline-offset: 2px;
    border-radius: 8px;
  }

  &:active {
    & > .comment-badge-inner {
      min-width: 28px;
      height: 28px;
      background-color: var(--menu-color-active);
    }
  }
`;

const BadgeButtonInner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 32px;
  height: 32px;
  padding: 0 4px;
  gap: 2px;
  border-radius: 30px;
  transition: all 0.2s ease-in-out;
  svg {
    height: 28px;
    width: 28px;
  }
`;

const CountText = styled.span<{ $isActive: boolean }>`
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  margin-left: -2px;
  color: ${({ $isActive }) => ($isActive ? "var(--primary-cta-high-contrast)" : "var(--grey-2)")};
`;

const COMMENT_ICON_STROKE_WIDTH = 1.7;

function ToolbarCommentIcon(): JSX.Element {
  return (
    <svg
      data-testid="comment-count-icon"
      aria-hidden="true"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.25 6.75H17.75C18.5784 6.75 19.25 7.42157 19.25 8.25V15C19.25 15.8284 18.5784 16.5 17.75 16.5H9.55L6.25 19.75V16.5C5.42157 16.5 4.75 15.8284 4.75 15V8.25C4.75 7.42157 5.42157 6.75 6.25 6.75Z"
        stroke="currentColor"
        strokeWidth={COMMENT_ICON_STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CommentCountBadge(): JSX.Element | null {
  const comments = useCommentStore((state) => state.comments);
  const commentCounts = useCommentStore((state) => state.commentCounts);
  /*
   * Subscribe to server open IDs, manuscript mark IDs and sync readiness separately: each can
   * arrive/change independently. The temporary composer annotation is also tracked so an unsaved
   * draft does not inflate the badge.
   */
  const openCommentAnnotationIds = useCommentStore((state) => state.openCommentAnnotationIds);
  const documentAnnotationIds = useCommentStore((state) => state.documentAnnotationIds);
  const documentAnnotationsReady = useCommentStore((state) => state.documentAnnotationsReady);
  const newAnnotationId = useCommentStore((state) => state.newAnnotationId);
  const { showComments, toggleComments } = useToggleCommentMode();
  const openComments = filterCommentsByType(comments, COMMENT_FILTER_TYPES.OPEN);

  const openCountFromTotals = Number(commentCounts?.[COMMENT_FILTER_TYPES.OPEN]);
  const paginatedOpenCount = Number.isFinite(openCountFromTotals) ? openCountFromTotals : null;
  /*
   * Count the intersection of current anchors and currently open rows, not just loaded cards or
   * all database rows. Keep the old server-total/page-length fallback before sync or when new
   * metadata is unavailable. Restoring a mark does not automatically reopen a resolved/ignored
   * row.
   */
  const count = getVisibleOpenCommentCount({
    comments,
    documentAnnotationIds,
    documentAnnotationsReady,
    openCommentAnnotationIds,
    newAnnotationId,
    fallbackCount: paginatedOpenCount ?? openComments.length,
  });
  const ariaLabel = count === 1 ? "1 comment" : `Comments (${count})`;

  const handleToggle = () => {
    toggleComments(!showComments);
  };

  return (
    <BadgeButtonOuter
      $isActive={showComments}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-pressed={showComments}
      onClick={handleToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleToggle();
        }
      }}
    >
      <BadgeButtonInner className="comment-badge-inner">
        <ToolbarCommentIcon />
        {!showComments && count > 0 && <CountText $isActive={showComments}>{count}</CountText>}
      </BadgeButtonInner>
    </BadgeButtonOuter>
  );
}

export default CommentCountBadge;
